import { useCallback, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
//old import { Layers, Loader2, ClipboardList, CheckCircle2, Building2, FileText, Mic, LogIn } from "lucide-react";
//old import { Layers, Loader2, ClipboardList, CheckCircle2, Building2, FileText, Mic, LogIn, KeyRound } from "lucide-react";
import { Layers, Loader2, ClipboardList, CheckCircle2, Building2, FileText, Mic, LogIn, KeyRound, ExternalLink } from "lucide-react";
import api from "../../api/api";
import CoachingTabs from "./CoachingTabs";
import { clearPendingJoin } from "../../utils/pendingJoin";
import SurveyModal from "../../components/SurveyModal";
import {
    Alert, EmptyState, Panel, Pill, Toggle, apiError, formatDate, useOrgStyles, ORG_TYPE_LABEL,
} from "../../components/org/orgUi";

// One box takes both kinds of code: a program's invitation code, or the
// student's own license code from their organization (single use).
const CODE_ERRORS = {
    //old INVALID_INVITE_CODE: "That code isn't valid. It may have expired or been removed, or its program has ended. Check it with your school or organization.",
    // only license codes now (a program's code is refused too, Alicia, Oct 30), so nothing about a program ending
    INVALID_INVITE_CODE: "That code isn't valid. It may have expired or been removed. Check it with your school or organization.",
    CODE_USED: "That code was already used by another account. Ask your school or organization for your own code.",
    ALREADY_LICENSED: "You already have a license from this organization, so you don't need this code.",
};
const codeError = (err, fallback) => CODE_ERRORS[err?.response?.data?.code] || apiError(err, fallback);

// The dates on a program's card. Past a program's own end date a student can still join it while one of its
// cohorts is running or still to come (Alicia, Oct 2: the program repeats every term and the cohort has the
// dates); the backend then gives that cohort's dates (CohortDates) and the card shows them, not the program's.
const cardDates = (p) => {
    const d = p.CohortDates || p;
    return `${formatDate(d.StartDate) || "…"} – ${formatDate(d.EndDate) || "ongoing"}`;
};

/**
 * My Coaching -> Programs: join a program with an invitation code, see the
 * programs and assessments from your organizations, and choose what each
 * organization's coaches may see.
 */
export default function MyPrograms() {
    const s = useOrgStyles();
    const [context, setContext] = useState(null);
    const [assessments, setAssessments] = useState([]);
    const [resources, setResources] = useState([]);
    const [error, setError] = useState("");
    const [notice, setNotice] = useState("");
    const [code, setCode] = useState("");
    const [preview, setPreview] = useState(null);
    const [busy, setBusy] = useState(false);
    const [activeSurvey, setActiveSurvey] = useState(null);
    const [searchParams, setSearchParams] = useSearchParams();

    const load = useCallback(async () => {
        try {
            //old const [ctx, asm] = await Promise.all([api.get("/org/context"), api.get("/org/my/assessments")]);
            // Program resources (Evan, Oct 9) are best effort: if they fail, the rest of the page still loads
            const [ctx, asm, rsc] = await Promise.all([api.get("/org/context"), api.get("/org/my/assessments"),
                                                       api.get("/org/my/resources").catch(() => null)]);
            setContext(ctx.data);
            setAssessments(asm.data.assessments || []);
            setResources(rsc?.data?.resources || []);
        } catch (err) {
            setError(apiError(err, "Couldn't load your programs."));
            setContext({ organizations: [], programs: [] });
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const lookupCode = useCallback(async (value) => {
        setError("");
        setPreview(null);
        setBusy(true);
        try {
            const res = await api.get(`/org/programs/lookup?code=${encodeURIComponent(value.trim())}`);
            //old setPreview(res.data.program);
            // A license code comes back as { kind: "license", OrgName, ProgramName, alreadyRedeemed }
            setPreview(res.data.kind === "license" ? res.data : res.data.program);
        } catch (err) {
            //old setError(apiError(err, "That invitation code isn't valid."));
            setError(codeError(err, "That code isn't valid."));
        } finally {
            setBusy(false);
        }
    }, []);

    const lookup = (e) => {
        e.preventDefault();
        lookupCode(code);
    };

    // Opened from an invitation link (/join/<code>): fill in the code and show the
    // program, then drop it from the URL so a refresh doesn't look it up again.
    useEffect(() => {
        const fromLink = (searchParams.get("code") || "").trim();
        if (!fromLink) return;
        clearPendingJoin(); // the invitation link is being used now
        setCode(fromLink.toUpperCase());
        lookupCode(fromLink);
        const next = new URLSearchParams(searchParams);
        next.delete("code");
        setSearchParams(next, { replace: true });
    }, [searchParams, setSearchParams, lookupCode]);

    const join = async () => {
        setBusy(true);
        try {
            const res = await api.post("/org/programs/join", { code: code.trim() });
            //old setNotice(res.data.alreadyEnrolled
            //old     ? `You're already in ${res.data.program.ProgramName}.`
            //old     : `You joined ${res.data.program.ProgramName}.`);
            if (res.data.kind === "license") {
                const inProgram = res.data.programName ? ` You're in ${res.data.programName}.` : "";
                setNotice(res.data.alreadyRedeemed
                    ? `You already used this code. Your license from ${res.data.orgName} is active.${inProgram}`
                    : `Your license from ${res.data.orgName} is active.${inProgram}`);
            } else {
                setNotice(res.data.alreadyEnrolled
                    ? `You're already in ${res.data.program.ProgramName}.`
                    : `You joined ${res.data.program.ProgramName}.`);
            }
            setCode("");
            setPreview(null);
            await load();
        } catch (err) {
            //old setError(apiError(err));
            setError(codeError(err));
        } finally {
            setBusy(false);
        }
    };

    const leave = async (program) => {
        //old if (!window.confirm(`Leave ${program.ProgramName}? You can rejoin later with the invitation code.`)) return;
        // no invitation code to come back with: the organization's admin adds them again (Alicia, Oct 30)
        if (!window.confirm(`Leave ${program.ProgramName}? To rejoin, ask your school or organization to add you again.`)) return;
        try {
            await api.delete(`/org/my/programs/${program.ProgramID}`);
            await load();
        } catch (err) {
            setError(apiError(err));
        }
    };

    const setShare = async (org, key, value) => {
        const col = { shareResume: "ShareResume", shareQuarterlyResults: "ShareQuarterlyResults", shareProgramProgress: "ShareProgramProgress" }[key];
        setContext((c) => ({ ...c, organizations: c.organizations.map((o) => (o.OrgID === org.OrgID ? { ...o, [col]: value ? 1 : 0 } : o)) }));
        try {
            await api.put(`/org/my/orgs/${org.OrgID}/sharing`, { [key]: value });
        } catch (err) {
            setError(apiError(err));
            load();
        }
    };

    const takeAssessment = async (a) => {
        try {
            const res = await api.get(`/survey/public/${a.SurveyID}`);
            setActiveSurvey(res.data.survey);
        } catch (err) {
            setError(apiError(err, "Couldn't open that assessment."));
        }
    };

    const loading = !context;
    const orgs = (context?.organizations || []);
    const programs = (context?.programs || []);

    return (
        <div className="min-h-screen bg-surface">
            <div className="mx-auto max-w-5xl px-4 py-6 md:py-8">
                <header className="mb-6">
                    <h1 className={`mb-2 text-2xl font-extrabold tracking-tight md:text-3xl ${s.text}`}>Programs</h1>
                    <p className={s.muted}>Programs from your school or employer, their assessments, and what you share with their coaches.</p>
                </header>
                <CoachingTabs className="mb-6 md:hidden" />
                <Alert onClose={() => setError("")}>{error}</Alert>
                <Alert kind="success" onClose={() => setNotice("")}>{notice}</Alert>

                {/* old <Panel title="Join a program" className="mb-6"> */}
                <Panel title="Enter a code" className="mb-6">
                    {/* old <p className={`mb-3 text-sm ${s.muted}`}>A program's invitation code, or your own license code from your school or organization.</p> */}
                    {/* license codes only: a program has no invitation code any more (Alicia, Oct 30) */}
                    <p className={`mb-3 text-sm ${s.muted}`}>Your own license code from your school or organization.</p>
                    <form onSubmit={lookup} className="flex flex-col gap-2 sm:flex-row">
                        <input
                            //old aria-label="Invitation code"
                            //old placeholder="Enter your invitation code"
                            aria-label="Code"
                            placeholder="Enter your code"
                            value={code}
                            onChange={(e) => { setCode(e.target.value.toUpperCase()); setPreview(null); }}
                            className={`${s.input} font-mono uppercase tracking-wider placeholder:font-sans placeholder:normal-case placeholder:tracking-normal sm:max-w-xs`}
                            maxLength={32}
                        />
                        <button type="submit" className={s.btnSecondary} disabled={busy || code.trim().length < 4}>
                            {/* old {busy && !preview ? <Loader2 size={14} className="animate-spin" /> : null} Find program */}
                            {busy && !preview ? <Loader2 size={14} className="animate-spin" /> : null} Check code
                        </button>
                    </form>
                    {/* A license code: the student's own seat from their organization */}
                    {preview?.kind === "license" && (
                        <div className={`mt-4 flex flex-col gap-3 rounded-xl border p-4 sm:flex-row sm:items-center sm:justify-between ${s.subPanel}`}>
                            <div>
                                <div className={`font-semibold ${s.text}`}>License from {preview.OrgName}</div>
                                <div className={`text-sm ${s.muted}`}>
                                    {preview.ProgramName ? `Includes ${preview.ProgramName}. ` : ""}
                                    {/* the plan line only when the license comes with a plan */}
                                    {preview.alreadyRedeemed ? "You already used this code."
                                        : preview.PlanName ? `Your organization covers your ${preview.PlanName} plan while the license is active.` : ""}
                                </div>
                            </div>
                            <button type="button" className={s.btnPrimary} onClick={join} disabled={busy}>
                                <KeyRound size={16} /> Use this code
                            </button>
                        </div>
                    )}
                    {/* old {preview && ( */}
                    {preview && preview.kind !== "license" && (
                        <div className={`mt-4 flex flex-col gap-3 rounded-xl border p-4 sm:flex-row sm:items-center sm:justify-between ${s.subPanel}`}>
                            <div>
                                <div className={`font-semibold ${s.text}`}>{preview.ProgramName}</div>
                                <div className={`text-sm ${s.muted}`}>{preview.OrgName}{preview.Outcome ? ` · ${preview.Outcome}` : ""}</div>
                            </div>
                            <button type="button" className={s.btnPrimary} onClick={join} disabled={busy}>
                                <LogIn size={16} /> Join program
                            </button>
                        </div>
                    )}
                </Panel>

                {loading ? (
                    <div className="flex justify-center py-10"><Loader2 className={`animate-spin ${s.muted}`} /></div>
                ) : (
                    <div className="space-y-6">
                        <Panel title="My programs" padded={programs.length > 0}>
                            {programs.length === 0 ? (
                                <EmptyState icon={Layers} title="You're not in a program yet"
                                            //old body="If your school or employer gave you an invitation code, enter it above." />
                                            // the organization puts its clients in programs, like a class showing up in Canvas (Alicia, Oct 30)
                                            body="When your school or organization adds you to a program, it shows up here." />
                            ) : (
                                // grid-cols-1 so a long link or description wraps on phones instead of pushing the card off the screen
                                //old <div className="grid gap-4 md:grid-cols-2">
                                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                                    {programs.map((p) => (
                                        <div key={p.ProgramID} className={`rounded-xl border p-4 ${s.subPanel}`}>
                                            <div className="flex items-start justify-between gap-2">
                                                <div>
                                                    <div className={`font-bold ${s.text}`}>{p.ProgramName}</div>
                                                    <div className={`text-sm ${s.muted}`}>{p.OrgName}</div>
                                                </div>
                                                <button type="button" className={`text-xs ${s.btnDanger}`} onClick={() => leave(p)}>Leave</button>
                                            </div>
                                            {p.Outcome && <p className={`mt-2 text-sm ${s.body}`}>{p.Outcome}</p>}
                                            {(p.StartDate || p.EndDate) && (
                                                //old <p className={`mt-1 text-xs ${s.muted}`}>{formatDate(p.StartDate) || "…"} – {formatDate(p.EndDate) || "ongoing"}</p>
                                                <p className={`mt-1 text-xs ${s.muted}`}>{cardDates(p)}</p>
                                            )}
                                            {(p.HasResumeEvaluation || p.HasInterviewEvaluation) ? (
                                                <div className="mt-3 flex flex-wrap gap-3 text-sm">
                                                    {p.HasResumeEvaluation ? <Link to="/resume-analysis" className={`inline-flex items-center gap-1 ${s.btnLink}`}><FileText size={14} /> Review my resume with program criteria</Link> : null}
                                                    {p.HasInterviewEvaluation ? <Link to="/mockInterview" className={`inline-flex items-center gap-1 ${s.btnLink}`}><Mic size={14} /> Practice an interview</Link> : null}
                                                </div>
                                            ) : null}
                                            {/* The program's links: the student's cohort's own list, else the program's */}
                                            {resources.some((r) => r.ProgramID === p.ProgramID) && (
                                                <div className="mt-3">
                                                    <div className={`mb-1.5 text-xs font-semibold uppercase tracking-wide ${s.muted}`}>Resources</div>
                                                    <ul className="space-y-1.5">
                                                        {resources.filter((r) => r.ProgramID === p.ProgramID).map((r) => (
                                                            <li key={r.ProgramResourceID}>
                                                                <a href={r.Url} target="_blank" rel="noopener noreferrer"
                                                                   className={`inline-flex max-w-full items-start gap-1 ${s.btnLink}`}>
                                                                    <ExternalLink size={14} className="mt-0.5 shrink-0" />
                                                                    <span className="min-w-0 break-words">{r.Title}</span>
                                                                </a>
                                                                {r.Description && <p className={`whitespace-pre-line break-words text-xs ${s.muted}`}>{r.Description}</p>}
                                                            </li>
                                                        ))}
                                                    </ul>
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </Panel>

                        {assessments.length > 0 && (
                            <Panel title="Assessments & surveys" padded={false}>
                                <ul className={`divide-y ${s.divider}`}>
                                    {assessments.map((a) => (
                                        <li key={`${a.SurveyID}-${a.ProgramID || a.OrgID}`} className="flex flex-col gap-2 px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
                                            <div className="flex items-start gap-3">
                                                <ClipboardList size={18} className={`mt-0.5 ${s.muted}`} />
                                                <div>
                                                    <div className={`font-semibold ${s.text}`}>{a.Title}</div>
                                                    <div className={`text-xs ${s.muted}`}>{a.ProgramName || a.OrgName}</div>
                                                </div>
                                            </div>
                                            {a.Completed
                                                ? <span className="inline-flex items-center gap-1 text-sm font-semibold text-emerald-600"><CheckCircle2 size={16} /> Completed</span>
                                                : <button type="button" className={s.btnPrimary} onClick={() => takeAssessment(a)}>Start</button>}
                                        </li>
                                    ))}
                                </ul>
                            </Panel>
                        )}

                        {orgs.length > 0 && (
                            <Panel title="Share with your organization's coaches">
                                <p className={`mb-4 text-sm ${s.muted}`}>
                                    You choose what each organization's coaches can see. You can change this any time.
                                </p>
                                <div className="space-y-4">
                                    {orgs.map((o) => (
                                        <div key={o.OrgID} className={`rounded-xl border p-4 ${s.subPanel}`}>
                                            <div className="mb-2 flex flex-wrap items-center gap-2">
                                                <Building2 size={16} className={s.muted} />
                                                <span className={`font-semibold ${s.text}`}>{o.OrgName}</span>
                                                <Pill>{ORG_TYPE_LABEL[o.OrgType]}</Pill>
                                                {o.HasLicense ? <Pill tone="success">{o.SponsoredTierName ? `Sponsors your ${o.SponsoredTierName} plan` : "Licensed"}</Pill> : null}
                                            </div>
                                            {/* old {!o.HasLicense && !!o.GivesStudentLicenses && ( */}
                                            {/* not for someone who already has the plan its client licenses give, or a dearer one (JJ, Nov 2: they have it already) */}
                                            {!o.HasLicense && !!o.GivesStudentLicenses && !o.HasOrgPlanOrBetter && (
                                                <p className={`mb-2 text-xs ${s.muted}`}>
                                                    {/* old {o.OrgName} hasn't given you a license yet. If they gave you a license code, enter it above, or you can{" "} */}
                                                    {/* old <Link to="/membership/plans" className={s.btnLink}>upgrade your own plan</Link>. */}
                                                    {/* JJ, Nov 2: the plan its client licenses give, at its own price, its checkout started at once
                                                        (UpgradeTierID); one a client can't buy on their own: My Membership */}
                                                    {o.OrgName} hasn't given you a license yet.{" "}
                                                    <Link to={o.UpgradeTierID ? `/membership/plans?plan=${o.UpgradeTierID}` : "/membership"}
                                                          className={`${s.btnSm} align-middle`}>Upgrade my plan</Link>
                                                    {" "}or wait for them to give you one.
                                                </p>
                                            )}
                                            {o.OrgType === "education" && (
                                                <p className={`mb-2 text-xs ${s.muted}`}>This organization can see that you're enrolled, but not your details, even if you share them.</p>
                                            )}
                                            <div className={`divide-y ${s.divider}`}>
                                                <Toggle label="Resume reviews" description="Your resume analyses and scores."
                                                        checked={!!o.ShareResume} onChange={(v) => setShare(o, "shareResume", v)} />
                                                <Toggle label="Quarterly results" description="How many of your quarterly goals you've completed."
                                                        checked={!!o.ShareQuarterlyResults} onChange={(v) => setShare(o, "shareQuarterlyResults", v)} />
                                                <Toggle label="Program progress" description="Assessments completed, and resume and interview practice in their programs."
                                                        checked={!!o.ShareProgramProgress} onChange={(v) => setShare(o, "shareProgramProgress", v)} />
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </Panel>
                        )}
                    </div>
                )}
            </div>
            {activeSurvey && (
                <SurveyModal
                    surveyData={activeSurvey}
                    onDismiss={() => setActiveSurvey(null)}
                    onComplete={() => { setActiveSurvey(null); load(); }}
                />
            )}
        </div>
    );
}
